/**
 * Echo Hunt -- the shared reshape prompt (P7, §6).
 *
 * VISIBILITY-MATRIX ROW (§16.0): "Shared reshape".
 *  - knows real/fake?      NO. This is the whole point of the step (§2 invariant #3, "same treatment
 *                          for both classes"): the prompt is built from the bullet TEXT and the seed
 *                          words only, so a real and a fake bullet cannot be treated differently --
 *                          there is no parameter through which a class could even be passed.
 *  - sees world/character?  NO (invariant #4).
 *  - sees other bullets?    NO (§6: one bullet per call).
 *  - sees corpus?           NO.
 *
 * ## Why every bullet is reshaped, including the real ones
 *
 * Reshaping seeds unusual material into EVERY bullet, not just the fakes (§6). Real dream bullets
 * already contain an uneven amount of oddness; adding a couple of seeded words to all of them erases
 * the "unusual object appeared -> must be fake" rule the user would otherwise learn. It also means the
 * word count *k* varies across the whole story, so no counting rule gives certainty either.
 *
 * ## The words are PERIPHERAL, not a replacement (§6)
 *
 * The core event must survive -- it is what the retention check (`Retention.ts`) verifies for real
 * bullets -- and the words go in as separate, added details. Fusing them into a hybrid ("a trombone
 * made of moss") is explicitly banned: it is the most conspicuous possible way to use two words, and
 * it is the shape a language model reaches for by default.
 *
 * ## The WORDING must change (2026-10-21)
 *
 * The step is also the pipeline's paraphrase: same event, told in new words. Before this, every stage kept
 * the phrasing along with the event, and runs of 5-10 of the user's own words reached the final text -- a
 * shallow tell ("I wrote that sentence") that bypasses the familiarity the game trains. Paraphrasing here,
 * fresh each round, rather than once in a cached pass, matters: a cached paraphrase is a FIXED phrasing, and
 * once seen at a reveal it would become a tell of its own. The deterministic check that enforces it is
 * `PhraseOverlap.ts`; on a leak, the retry quotes the offending phrases back (`avoidPhrases`).
 *
 * Symmetry still holds: the paraphrase instruction is in every prompt, and a fake's retry quotes ITS leaks
 * (against its own generator text) in exactly the same section a real's retry does.
 *
 * See `Docs/Plans/Others/2026-10-19_EchoHunt.md` §6, §2 (#3), §16.2 P7.
 */
import {JSONSchemaLike} from "../LLM.js";
import {HOUSE_STYLE_RULES} from "./IndexPrompt.js";

/** The retelling rules, stated in EVERY reshape prompt (seeded or not). */
export const RESHAPE_RETELL_RULES = [
	"Keep the CORE event: the same actions, objects, places and people, in the same order.",
	"Tell it in your OWN words. Do not reuse the original's phrasing: no run of four or more of its words in a row, apart from names and unavoidable short phrases.",
	"Change the sentence structure too, not just individual words -- reorder clauses, change which detail comes first, switch between active and passive where it reads naturally.",
	"Plain, ordinary wording. Do not reach for rare synonyms or a fancier register just to be different; it should read like someone else remembering the same thing.",
	"Do not lengthen the bullet much: a short bullet stays short.",
	"Do not add anything new beyond what is asked: no extra characters, no new places, no extra turns of events.",
].map(a=>`- ${a}`).join("\n");

/** The seed-word rules (§6). Only stated when there ARE words to work in. */
export const RESHAPE_SEED_RULES = [
	"ADD the words as extra detail; do not use them to replace anything that was already there.",
	"Use each word as its own separate thing. Never fuse two of them into one object (\"a trombone made of moss\" is wrong).",
	"Put the words at the periphery of the event, the way an incidental detail gets remembered -- not as the point of it.",
	"Do not explain the words, comment on them, or make them meaningful. They are just things that were there.",
	"One brief mention per word is enough.",
].map(a=>`- ${a}`).join("\n");

export function Reshape_SystemPrompt(): string {
	return [
		"You retell single sentences describing a remembered event, in fresh wording, sometimes adding a few given details.",
		"You keep the original event intact while changing how it is told.",
		"Reply with JSON only. No prose, no code fences, no explanation.",
	].join("\n");
}

export type Reshape_UserPromptOptions = {
	/** the bullet to reshape. For a real bullet this is its indexed `selfContainedText`; for a fake it
	 * is the generator's output. The prompt cannot tell which. */
	bulletText: string;
	/** the words that must be worked in (§6). 1-2 in a seeded round; EMPTY in an unseeded round, which
	 * still calls this step so the wording gets changed (2026-10-21). */
	seedWords: string[];
	/** on a retry: phrases the previous attempt copied from the original (`PhraseOverlap.FindPhraseLeaks`).
	 * Computed the same way for both classes, so this section cannot become a class tell. */
	avoidPhrases?: string[]|n;
};

/** Builds the reshape prompt. IMPORTANT: this function takes only the bullet text and the words, so
 * the prompt for a real bullet and for a fake bullet are byte-identical when those two inputs match.
 * `Reshape.test.ts` pins that (§16.2 P7). */
export function Reshape_UserPrompt(opts: Reshape_UserPromptOptions): string {
	const {bulletText, seedWords, avoidPhrases} = opts;
	const seeded = seedWords.length > 0;

	const parts = [
		"## Task",
		seeded
			? "Retell the bullet below in fresh wording, and work in the given words."
			: "Retell the bullet below in fresh wording.",
		"The event itself must still be the same event; only the way it is told changes.",
		"",
		"## The bullet",
		bulletText.trim(),
		"",
	];

	if (seeded) {
		parts.push(
			"## Words to work in",
			...seedWords.map(word=>`- ${word}`),
			"",
		);
	}

	parts.push(
		"## Rules",
		RESHAPE_RETELL_RULES,
		...(seeded ? [RESHAPE_SEED_RULES] : []),
		"",
		"## House style",
		HOUSE_STYLE_RULES,
		"",
	);

	const avoid = (avoidPhrases ?? []).map(a=>a.trim()).filter(a=>a.length > 0);
	if (avoid.length > 0) {
		parts.push(
			"## Fix this problem from your previous attempt",
			"It copied these phrases word-for-word from the bullet. Say each of these parts differently this time:",
			...avoid.map(phrase=>`- "${phrase}"`),
			"",
		);
	}

	parts.push(
		"## Output",
		"Reply with ONE JSON object: {\"text\": \"...\"}",
		"The `text` value is the retold bullet: one event, no heading, no bullet character, no quotation marks.",
	);

	return parts.join("\n");
}

/** The JSON shape `CallLLM` validates each attempt against. */
export function Reshape_JSONSchema(): JSONSchemaLike {
	return {
		type: "object",
		required: ["text"],
		properties: {
			text: {type: "string", minLength: 1},
		},
	};
}
